'use client'

import React, { useEffect, useState } from 'react'
import classNames from 'classnames'
import { Header as HeaderType, Media as MediaType, Page } from '@/payload-types'
import { CMSImage } from '@/ui/atoms/CMSImage'
import { usePathname } from 'next/navigation'
import { removeLangFromPath } from '@/utils/removeLangFromPath'
import { Toggle } from '@/ui/layout/Header/NavBar/Toggle'
import Link from 'next/link'
import { NavAuth } from '@/ui/layout/Header/NavBar/Auth'
import { MaxWidthGlobal } from '@/ui/layout/Gutter/new/MaxWidthGlobal'
import { useLocale, useTranslations } from 'next-intl'
import { MegaMenu } from './MegaMenu'
import { NavItem } from './NavItem'
import { AreaStructure } from '@/utils/getAreaStructure'
import { localizedHref } from '@/utils/localizedHref'
import { mapAreaStructureToMegaMenuItem } from './MegaMenu/mapToMegaMenuItems'

type NavBarProps = {
  headerData: HeaderType
  appearance?: 'whiteBlur' | 'colorGradient'
  areaStructure?: AreaStructure
  className?: string
}

const getAppearanceForPath = (path: string): 'whiteBlur' | 'colorGradient' => {
  // Add your path-based logic here
  // For example:

  if (
    path === '/' ||
    path === '' ||
    path.startsWith('/dashboard') ||
    path.startsWith('/website') ||
    // workflow hub pages: /workflows/<domain>/<workflow> (P2 masthead; the handbook templates
    // open on a white title block and keep the default bar, docs/style-guide)
    (path.startsWith('/workflows/') && path.split('/').length === 4 && process.env.NEXT_PUBLIC_STYLE_GUIDE !== '1')
  ) {
    return 'whiteBlur'
  }
  return 'colorGradient'
}

const shouldEnableScrollListener = (path: string): boolean => {
  // Add paths where you want the scroll listener to be active
  return (
    path === '/' ||
    path === '' ||
    path.startsWith('/dashboard') ||
    path.startsWith('/website') ||
    // workflow hub pages: /workflows/<domain>/<workflow> (P2 masthead; the handbook templates
    // open on a white title block and keep the default bar, docs/style-guide)
    (path.startsWith('/workflows/') && path.split('/').length === 4 && process.env.NEXT_PUBLIC_STYLE_GUIDE !== '1')
  )
}

const breadcrumbsShown = (path: string): boolean => {
  // Add paths where you want the scroll listener to be active
  return !path.startsWith('/dashboard') && !path.startsWith('/website')
}

export const NavBar: React.FC<NavBarProps> = ({ className: classNameFromProps, ...props }) => {
  const { headerData, areaStructure, appearance: appearanceFromProps } = props
  const { logo, navItems } = headerData

  const [isOpen, setIsOpen] = useState(false)
  const [lastScrollY, setLastScrollY] = useState(0)
  const [isVisible, setIsVisible] = useState(true)
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMegaMenuOpen, setIsMegaMenuOpen] = useState(false)

  const pathName = usePathname()
  const pathBasedAppearance = getAppearanceForPath(removeLangFromPath(pathName))

  const currentAppearance = appearanceFromProps || pathBasedAppearance
  const locale = useLocale()

  const logoProps = logo as MediaType

  // Define reusable style constants
  const GRADIENT_STYLE = 'bg-gradient-to-r from-[#5250B5] to-[#224383]'
  const WHITE_BLUR_STYLE = 'bg-white bg-opacity-15 backdrop-blur-xl'
  const t = useTranslations('megaMenu')

  // Determine appearance based on state conditions
  const appearance =
    isMegaMenuOpen || (currentAppearance === 'whiteBlur' && isScrolled)
      ? GRADIENT_STYLE
      : currentAppearance === 'colorGradient'
        ? GRADIENT_STYLE
        : WHITE_BLUR_STYLE

  const handleToggle = () => {
    setIsOpen((prev) => !prev)
  }

  const handleRichMenuClose = () => {
    setIsOpen(false)
  }

  const handleMegaMenuOpenChange = (isOpen: boolean) => {
    setIsMegaMenuOpen(isOpen)
  }

  useEffect(() => {
    setIsOpen(false)
  }, [pathName])

  useEffect(() => {
    const handleResize = () => {
      setIsOpen(false)
      document.body.classList.remove('overflow-hidden')
    }

    if (isOpen) {
      document.body.classList.add('overflow-hidden')
    } else {
      document.body.classList.remove('overflow-hidden')
    }

    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [isOpen])

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY

      // Update scroll state based on 100px threshold
      setIsScrolled(currentScrollY > 100)

      setLastScrollY(currentScrollY)
    }

    const cleanPath = removeLangFromPath(pathName)
    if (shouldEnableScrollListener(cleanPath)) {
      window.addEventListener('scroll', handleScroll, { passive: true })
      return () => {
        window.removeEventListener('scroll', handleScroll)
      }
    }
  }, [lastScrollY, pathName])

  return (
    <nav
      className={classNames(
        classNameFromProps,
        // site-nav: hook for the mobile overflow-x clip in globals.css (Toggle's -m-3 tap-area
        // spill — see the comment there; Tailwind max-* variants are unavailable in this config).
        'site-nav z-[45] fixed w-full px-4 lg:px-5 py-4 lg:py-5 transition-transform duration-300 mb-mobile-grid-gutter lg:mb-grid-gutter',
        {
          'transform -translate-y-full': !isVisible,
          '!bg-gradient-to-t from-transparent to-bg-base': currentAppearance === 'colorGradient',
          '!pb-0': !breadcrumbsShown(pathName),
        },
      )}
    >
      {/*<div*/}
      {/*  className={`${currentAppearance === 'colorGradient' && 'w-screen h-5 fixed top-0 inset-x-0 bg-white bg-opacity-90 backdrop-blur z-40'}`}*/}
      {/*></div>*/}

      <div
        className={classNames(
          'h-auto w-auto rounded-md p-3 py-2 transition-colors duration-300',
          {
            'fixed inset-0 rounded-none p-3 bg-gradient-to-r from-[#5250B5] to-[#224383]': isOpen,
          },
          !isOpen && appearance,
        )}
        style={{ zIndex: 50 }}
      >
        <MaxWidthGlobal
          className={`flex justify-between items-center ${isOpen ? 'p-4 lg:p-0' : ''}`}
        >
          <Link href={localizedHref('/', locale)} className={'z-50'}>
            <CMSImage
              resource={logoProps}
              loading={'eager'}
              priority={true}
              className={classNames('h-8 w-auto')}
              onClick={handleToggle}
            />
          </Link>
          <Toggle isOpen={isOpen} onClick={handleToggle} className={'z-50 block lg:hidden'} />
          <ul
            className={classNames(
              'flex flex-col lg:flex-row list-none lg:pt-0 lg:px-5 px-4 lg:px-0',
              {
                'hidden lg:flex': !isOpen,
                'fixed inset-0 top-24 pb-6 lg:pb-0 z-40 overflow-y-auto': isOpen,
              },
            )}
          >
            {navItems?.map((item, i) => {
              const referencePath =
                (item.button?.link?.reference?.value as Page)?.path ?? item.button?.link?.url

              const isActive =
                referencePath && referencePath.startsWith('/')
                  ? removeLangFromPath(pathName) === removeLangFromPath(referencePath)
                  : false

              // WORKFLOWS LANDER DEMO (2026-07-08): with the flag on, the mega-menu item
              // becomes a plain link to the /workflows lander instead of a dropdown.
              const workflowsLander = process.env.NEXT_PUBLIC_WORKFLOWS_LANDER === '1'

              // Check if this nav item should show the MegaMenu
              const shouldShowMegaMenu = item.showMegaMenu && areaStructure && !workflowsLander

              return (
                <li
                  key={i}
                  className="lg:ml-2 m-0 mb-4 lg:mb-0 lg:text-label-XS-semibold flex justify-start lg:justify-center items-center"
                >
                  {shouldShowMegaMenu ? (
                    <MegaMenu
                      trigger={
                        <NavItem
                          label={item.menuLabel || undefined}
                          isMobile={isOpen}
                          isActive={isMegaMenuOpen}
                          showNextIcon={isOpen}
                          isDropdown={!isOpen}
                        />
                      }
                      data={mapAreaStructureToMegaMenuItem(areaStructure, locale)}
                      isMobile={isOpen}
                      onClose={handleRichMenuClose}
                      onOpenChange={handleMegaMenuOpenChange}
                    />
                  ) : item.showMegaMenu && workflowsLander ? (
                    <NavItem
                      href={localizedHref('/workflows', locale)}
                      label={item.menuLabel || 'Industry Workflows'}
                      isActive={removeLangFromPath(pathName) === '/workflows'}
                      isMobile={isOpen}
                    />
                  ) : (
                    <NavItem button={item.button} isActive={isActive} isMobile={isOpen} />
                  )}
                </li>
              )
            })}
            <NavAuth
              className={
                'lg:ms-4 mt-auto lg:mt-none border-t border-t-border-subtle-transparent-10 lg:border-none pt-3 lg:pt-0'
              }
              isMobile={isOpen}
            />
          </ul>
        </MaxWidthGlobal>
      </div>
    </nav>
  )
}
